import {Component, OnInit, Optional, ChangeDetectionStrategy} from '@angular/core';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {DateRangeSelectorComponent} from '../../../layout/date-range-selector/date-range-selector.component';
import {DateRange} from '../../../../types/date-range';
import {DropdownSelectComponent} from '../../../layout/dropdown-select/dropdown-select.component';
import {
  DropdownSelectOptionComponent
} from '../../../layout/dropdown-select-option/dropdown-select-option.component';
import { DatePipe, NgClass } from '@angular/common';
import {PartnerService} from '../../../../services/partner/partner.service';
import {Partner} from '../../../../models/partner/partner';
import {SnackbarService} from '../../../../services/snackbar/snackbar.service';
import {ConfirmService} from '../../../../services/confirm/confirm.service';
import {Paginator} from '../../../../tools/pagination/paginator';
import {TableSorter} from '../../../../tools/table-sorter/table-sorter';
import {LoaderService} from '../../../../services/loader/loader.service';
import {TableSorterDirective} from '../../../../directives/table-sorter/table-sorter.directive';
import {TableSorterHeaderDirective} from '../../../../directives/table-sorter/table-sorter-header.directive';
import {PaginatorComponent} from '../../../layout/paginator/paginator.component';
import {forkJoin} from 'rxjs';
import {BillingRequestService} from '../../../../services/accounting/billing-request.service';
import {BillingRequest} from '../../../../models/accounting/billing-request';
import {PartnerTabComponent} from '../partner-tab/partner-tab.component';
import {MetaTitle} from '../../../../decorator/meta-title';
import {ConfirmDirective} from '../../../../directives/confirm/confirm.directive';
import {DialogService} from '../../../../services/dialog/dialog.service';
import {InvoiceUpdateFormComponent} from './invoice-update-form/invoice-update-form.component';
import {PercentPipe} from '../../../../pipes/percent/percent.pipe';
import {MoneyPipe} from '../../../../pipes/money/money.pipe';
import {InvoiceService} from '../../../../services/accounting/invoice.service';
import {TranslatePipe} from '@ngx-translate/core';
import {AuthService} from '../../../../services/auth/auth.service';
import {Config} from '../../../../models/config';

@Component({
  selector: 'app-partner-payment',
  imports: [
    ReactiveFormsModule,
    DateRangeSelectorComponent,
    DropdownSelectComponent,
    DropdownSelectOptionComponent,
    FormsModule,
    DatePipe,
    TableSorterDirective,
    TableSorterHeaderDirective,
    PaginatorComponent,
    ConfirmDirective,
    PercentPipe,
    MoneyPipe,
    NgClass,
    TranslatePipe
],
  providers: [DatePipe, TranslatePipe],
  templateUrl: './partner-payment.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './partner-payment.component.scss'
})
@MetaTitle('Affiliate payment')
export class PartnerPaymentComponent implements OnInit {

  selectedBillingRequests: number[] = [];

  partner: Partner | null = null;
  paginator: Paginator = new Paginator();
  tableSorter: TableSorter = new TableSorter('endingAt', 'desc');
  downloadMode: 'csv' | 'zip' = 'zip';

  billingRequests: BillingRequest[] = [];
  invoiceStatusList: string[] = [];

  filters = new FormGroup({
    startingAt: new FormControl(),
    endingAt: new FormControl(),
    status: new FormControl([]),
    deferred: new FormControl(''),
  });

  constructor(
    protected invoiceService: InvoiceService,
    private auth: AuthService,
    private loader: LoaderService,
    private partnerService: PartnerService,
    private billingRequestService: BillingRequestService,
    private confirm: ConfirmService,
    private snackbar: SnackbarService,
    private dialog: DialogService,
    private datePipe: DatePipe,
    private translate: TranslatePipe,
    @Optional() private partnerTab: PartnerTabComponent
  ) {
  }

  ngOnInit(): void {
    this.auth.getAsyncConfig().subscribe((config: Config) => {
      this.invoiceStatusList = config?.data?.invoiceStatusList ?? [];
    });
  }

  isSelected(billingRequest: BillingRequest): boolean {
    return billingRequest.id ? this.selectedBillingRequests.includes(billingRequest.id) : false;
  }

  fetchAll(): void {
    if (!this.partner || !this.partner.id) {
      return;
    }
    this.loader.startLoading();

    const data: any = this.filters.value;
    const parameters: any = {
      partnerId: this.partner.id,
      groups: 'file'
    };

    if (data.startingAt) {
      parameters.startingAt = this.datePipe.transform(data.startingAt, 'yyyy-MM-dd');
    }
    if (data.endingAt) {
      parameters.endingAt = this.datePipe.transform(data.endingAt, 'yyyy-MM-dd');
    }
    if (data.status && data.status.length) {
      parameters.status = data.status;
    }
    if (data.deferred !== '') {
      parameters.deferred = data.deferred;
    }

    this.billingRequestService.findAll(parameters, this.paginator, this.tableSorter).subscribe((billingRequests: BillingRequest[]) => {
      this.billingRequests = billingRequests;

      this.loader.stopLoading();
    });
  }

  onDateRangeInit(dateRange: DateRange): void {
    this.updateRange(dateRange);

    this.partnerTab.partnerSubject.subscribe((partner: Partner | null) => {
      this.partner = partner;
      this.fetchAll();
    });
  }

  updateRange(dateRange: DateRange): void {
    this.filters.get('startingAt')?.patchValue(dateRange.start);
    this.filters.get('endingAt')?.patchValue(dateRange.end);
  }

  toggleSelectAll(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) {
      this.selectedBillingRequests = this.billingRequests
        .map((billingRequest: BillingRequest) => billingRequest.id)
        .filter((id): id is number => id !== null);
    } else {
      this.selectedBillingRequests = [];
    }
  }

  selectBillingRequest(billingRequest: BillingRequest): void {
    if (billingRequest.id) {
      if (this.selectedBillingRequests.includes(billingRequest.id)) {
        const index = this.selectedBillingRequests.indexOf(billingRequest.id);
        this.selectedBillingRequests.splice(index, 1);
      } else {
        this.selectedBillingRequests.push(billingRequest.id)
      }
    }
  }

  isAllSelected(): boolean {
    return this.selectedBillingRequests.length === this.billingRequests.length && this.billingRequests.length > 0;
  }

  openBillingLineModal(billingRequest: BillingRequest): void {
    this.dialog.openDialog(InvoiceUpdateFormComponent, {
      data: billingRequest,
      backdrop: false
    }).subscribe((submitted: boolean) => {
      if (submitted) {
        this.fetchAll();
      }
    });
  }

  delete(billingRequest: BillingRequest): void {
    if (billingRequest.id) {
      this.billingRequestService.delete(billingRequest.id).subscribe(() => {
        this.fetchAll();
        this.snackbar.success('Invoice deleted.');
      });
    }
  }

  updateStatus(status: string, billingRequest: BillingRequest | null = null) {
    if (!this.selectedBillingRequests.length && !billingRequest) {
      return;
    }

    const ids = (billingRequest && billingRequest.id) ? [billingRequest.id] : this.selectedBillingRequests;
    const count = this.selectedBillingRequests.length;
    const confirmMessage = billingRequest ? 'invoice' : `selection of ${count} invoices`;
    const statusLabel = this.translate.transform('invoiceStatus.' + status);

    this.confirm.open(`Do you really want to change the status of this ${confirmMessage} to ${statusLabel}?`).subscribe((result: boolean) => {
      if (result) {
        const requests = [];
        const data: any = {
          status
        };

        for (const id of ids) {
          requests.push(this.invoiceService.update(Number(id), data));
        }

        forkJoin(requests).subscribe(() => {
          this.fetchAll();

          this.snackbar.success(`The status has been successfully changed to ${statusLabel}.`);
        });
      }
    });
  }
}
